import {getUserTokenOrRedirect} from "../../shared/services/auth.service";
import {NavigateFunction} from "react-router-dom";
import {CustomerFacingInvoiceDto} from "./dtos/customer-facing-invoice.dto";
import { InvoiceDto, InvoiceStatusUpdateDto, InvoiceSummaryDto, InvoiceUpdateDto } from "./dtos/invoice.dto";
import { savaquanFetchJson } from "../../shared/services/http.service";

export async function getInvoice(navigate: NavigateFunction, invoiceId: number): Promise<InvoiceDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const invoice = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/invoices/${invoiceId}`,
    {headers: {Authorization: `Bearer ${token}`}})
    .then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch invoices: ${response.statusText}`);
      }
      return await response.json() as InvoiceDto
    });
  return invoice;
}

export async function getInvoices(navigate: NavigateFunction): Promise<InvoiceDto[]> {
  const token = await getUserTokenOrRedirect(navigate)
  const invoices = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/invoices`,
    {headers: {Authorization: `Bearer ${token}`}})
    .then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch invoices: ${response.statusText}`);
      }
      return await response.json() as InvoiceDto[]
    });
  return invoices;
}

// SQ-625: the list rows without each invoice's job graph; what the
// Invoices page reads. getInvoices stays for anything that needs the job.
export async function getInvoiceSummaries(navigate: NavigateFunction): Promise<InvoiceSummaryDto[]> {
  return await savaquanFetchJson<InvoiceSummaryDto[]>(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/invoices/summaries`,
    {navigate});
}

export async function getCustomerFacingInvoice(accessToken: string, invoiceId: number): Promise<CustomerFacingInvoiceDto> {
  const invoice = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/invoices/external/${invoiceId}`,
    {headers: {Authorization: `Bearer ${accessToken}`}})
    .then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch invoice: ${response.statusText}`);
      }
      return await response.json() as CustomerFacingInvoiceDto
    });
  return invoice;
}

export async function emailInvoiceToCustomer(navigate: NavigateFunction, invoiceId: number): Promise<void> {
  const token = await getUserTokenOrRedirect(navigate)
  await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/invoices/${invoiceId}/send-to-customer`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to email invoice to customer: ${response.statusText}`);
      }
    });
}

export async function updateInvoice(navigate: NavigateFunction, invoiceId: number, updateDto: InvoiceUpdateDto): Promise<InvoiceDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const updatedInvoice = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/invoices/${invoiceId}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateDto)
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to update invoice status: ${response.statusText}`);
      }
      return await response.json() as InvoiceDto
    });
  return updatedInvoice;
}

export async function updateInvoiceStatus(navigate: NavigateFunction, invoiceId: number, updateDto: InvoiceStatusUpdateDto): Promise<InvoiceDto> {
  return await savaquanFetchJson<InvoiceDto>(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/invoices/${invoiceId}/status`,
    {
      navigate,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateDto)
    });
}

// SQ-429: records one payment (full or part). Returns the invoice with its
// payments; paying the remaining balance marks it Paid. A refused amount
// comes back as a 400 with the reason.
export async function recordInvoicePayment(navigate: NavigateFunction, invoiceId: number, amount: number): Promise<InvoiceDto> {
  return await savaquanFetchJson<InvoiceDto>(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/invoices/${invoiceId}/payments`,
    {
      navigate,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({amount})
    });
}

// SQ-688: stop or turn back on one invoice's follow-up emails (invoices.send).
// The answer is the invoice's new followUpsStoppedAt: a time, or null.
export async function setInvoiceFollowUpsStopped(navigate: NavigateFunction, invoiceId: number, stop: boolean): Promise<{stoppedAt: string | null}> {
  return await savaquanFetchJson<{stoppedAt: string | null}>(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/invoices/${invoiceId}/follow-ups/${stop ? 'stop' : 'resume'}`,
    {navigate, method: 'POST'});
}
